import {
  PlusOutlined,
} from '@ant-design/icons';
import {
  Alert,
  App,
  Button, DatePicker, Drawer, Flex,
  Form,
  Input,
  Select,
  Skeleton,
  Space,
  Table,
  Typography,
} from 'antd';
import { pluralize } from 'components/ItemsInput/AddOrderItemOverlay/utils';
import DebounceSelect from 'components/Select/DebounceSelect';
import SelectAuthorizedEmail from 'components/SelectAuthorizedEmail';
import useFetchedData from 'hooks/useFetchedData';
import useIsMobile from 'hooks/useIsMobile';
import useLoadings from 'hooks/useLoadings';
import React, {
  useMemo,
  useState,
} from 'react';
import http from 'utils/http';
import Togglable from 'components/TogglableInput';
import dayjs from 'dayjs';
import ActionTimeline from '../../../../components/ActionTimeline';
import ContactDrawerForm from '../../ContactDrawerForm';
import ExpandableSearchInput from './ExpandableSearchInput';
import { contactsTableColumns, serializeFormData } from './utils';

const COMPANY_OWNER_EMAIL = {
  value: 'company_owner',
  label: 'Company Owner',
};
const FIXED_EMAIL_OPTIONS = [COMPANY_OWNER_EMAIL];
const { Text } = Typography;

const RUN_MODES = {
  COMPANY_LIST: 'company_list',
  CONTACT_LIST: 'contact_list',
  COMPANY: 'company',
};

function getRunMode(companyId, contactFilters) {
  if (companyId) {
    return RUN_MODES.COMPANY;
  }
  if (contactFilters) {
    return RUN_MODES.CONTACT_LIST;
  }

  return RUN_MODES.COMPANY_LIST;
}

function RunSequenceDrawer({
  open,
  onClose,
  onFinish,
  companyId,
  companyFilters,
  contactFilters,
  companiesCount,
  selections,
}) {
  const { message } = App.useApp();
  const isMobile = useIsMobile();
  const [selectedContactIds, setSelectedContactIds] = useState(null);
  const [createContactOpen, setCreateContactOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedSequenceId, setSelectedSequenceId] = useState(null);
  const [currentStep, setCurrentStep] = useState(1); // 1 = sequence selection, 2 = contact selection
  const [selectedSenderEmail, setSelectedSenderEmail] = useState();
  const [selectedContactType, setSelectedContactType] = useState('primary');
  const [form] = Form.useForm();
  const [actionFormState, setActionFormState] = useState({});
  const { data: sequence, loading: sequenceLoading } = useFetchedData(selectedSequenceId && `v1/organizations/{{organizationId}}/workflows/${selectedSequenceId}/`, (data) => data?.data);
  const sequenceActions = useMemo(() => sequence?.actions || [], [sequence]);
  const runMode = getRunMode(companyId, contactFilters);
  const [scheduledFor, setScheduledFor] = useState(null);
  const disabledDate = (current) => current && current < dayjs().startOf('day');

  const { data: contacts, loading: contactsLoading, mutate: refetchContacts } = useFetchedData(
    companyId && open ? `v1/organizations/{{organizationId}}/companies/${companyId}/contacts/?page_size=300` : null,
    ({ results: contacts } = {}) => contacts?.map((c) => ({
      id: c.id,
      name: `${c.first_name} ${c.last_name}`,
      email: c.email,
      role: c.role,
      phone: c.phone,
    })),
    { enabled: !!selectedSequenceId && currentStep === 2 },
  );
  const { setLoading, isLoading, resetLoadings } = useLoadings();

  // Reset state when drawer opens/closes
  React.useEffect(() => {
    if (open) {
      setSelectedSequenceId(null);
      setSelectedContactIds([]);
      setSearchText('');
      setCurrentStep(1);
      resetLoadings();
      setScheduledFor(null);
      // setSelectedSenderEmail(null);
      setActionFormState({});
      form.resetFields();
    }
  }, [open, form]);

  // Auto-validate actions when sequence actions change
  React.useEffect(() => {
    if (sequenceActions.length > 0) {
      const initialFormState = {};
      sequenceActions.forEach((action) => {
        if (!action.definition) {
          // No parameters at all - mark as valid
          initialFormState[action.id] = {
            form: null,
            isValid: true,
            definition: null,
          };
        } else {
          // Has parameters - will be validated by form
          initialFormState[action.id] = {
            form: null,
            isValid: false,
            definition: action.definition,
          };
        }
      });

      setActionFormState((prev) => ({
        ...prev,
        ...initialFormState,
      }));
    }
  }, [sequenceActions]);

  const fetchSequenceOptions = async (searchKey = '') => {
    try {
      const res = await http.get('v1/organizations/{{organizationId}}/workflows/', {
        params: { search: searchKey },
      });

      const sequences = res.data?.results || [];

      // Return options for DebounceSelect
      return sequences.map((sequence) => ({
        value: sequence.id,
        label: sequence.name || 'Unnamed Sequence',
      }));
    } catch (e) {
      return [];
    }
  };

  const handleSequenceChange = (newSequenceId) => {
    // Find the full sequence object from our stored sequences
    setSelectedSequenceId(newSequenceId);
    form.resetFields();

    // Reset action forms and validation states
    setActionFormState({});
  };

  // Handle form changes for action parameters
  const handleActionFormsChange = ({
    actionId, form: actionForm, isValid, definition,
  }) => {
    setActionFormState((prev) => ({
      ...prev,
      [actionId]: {
        form: actionForm,
        isValid,
        definition,
      },
    }));
  };

  // Validate all action parameter forms
  const validateAllActionForms = async () => {
    const formValidationPromises = Object.entries(actionFormState)
      .filter(([, { form }]) => form !== null) // Only validate forms that exist
      .map(async ([actionId, { form }]) => {
        try {
          await form.validateFields();
          return { actionId: parseInt(actionId, 10), isValid: true };
        } catch (error) {
          return { actionId: parseInt(actionId, 10), isValid: false };
        }
      });

    const validationResults = await Promise.all(formValidationPromises);
    const allValid = validationResults.every((result) => result.isValid);

    // Update validation states
    const updatedFormState = { ...actionFormState };
    validationResults.forEach(({ actionId, isValid }) => {
      if (updatedFormState[actionId]) {
        updatedFormState[actionId] = {
          ...updatedFormState[actionId],
          isValid,
        };
      }
    });
    setActionFormState(updatedFormState);

    return allValid;
  };

  function handleRunSequence() {
    setLoading('run_sequence');

    // Collect all form values with proper serialization
    const actionParameters = {};
    Object.entries(actionFormState).forEach(([actionId, { form, definition }]) => {
      if (form) {
        const formData = form.getFieldsValue();

        if (definition) {
          // Serialize form data back to nested structure
          actionParameters[actionId] = serializeFormData(formData, definition);
        } else {
          // Fallback to flat structure if no definition
          actionParameters[actionId] = formData;
        }
      }
    });
    const data = {
      override_email: selectedSenderEmail?.value === COMPANY_OWNER_EMAIL.value ? null : selectedSenderEmail?.value,
      action_overrides: actionParameters,
    };
    if (scheduledFor) {
      data.scheduled_for = scheduledFor.toISOString();
    }

    if (runMode === RUN_MODES.COMPANY_LIST) {
      data.first_contacts_only = selectedContactType === 'primary';
      if (selections.selectAll) {
        data.company_filters = {
          ...Object.values(companyFilters).filter(Boolean).length > 0 ? companyFilters : { all: true },
          exclude_company_ids: selections.excludedIds,
        };
      } else {
        data.company_ids = selections.includedIds;
      }
    } else if (runMode === RUN_MODES.COMPANY) {
      data.contact_ids = selectedContactIds;
    } else if (runMode === RUN_MODES.CONTACT_LIST) {
      if (selections.selectAll) {
        data.contact_filters = {
          ...Object.values(contactFilters).filter(Boolean).length > 0 ? contactFilters : { all: true },
          exclude_contact_ids: selections.excludedIds,
        };
      } else {
        data.contact_ids = selections.includedIds;
      }
    }

    http.post(`/v1/organizations/{{organizationId}}/workflows/${selectedSequenceId}/run/`, data).then(() => {
      setLoading('run_sequence', false);
      message.success(`Sequence "${sequence.name}" has started successfully.`);
      if (onFinish) {
        onFinish();
      }
      onClose();
    }).catch((error) => {
      setLoading('run_sequence', false);
      message.error({
        content: error.response?.data?.detail ||
          'Failed to run sequence. Please try again or contact support if the problem persists.',
        duration: 5,
      });
    });
  }

  async function handleAddContact(data) {
    setLoading('create_contact');
    const { data: { id } } = await http.post(`/v1/organizations/{{organizationId}}/companies/${companyId}/contacts/`, data);
    setLoading('create_contact', false);
    refetchContacts();
    setSelectedContactIds((current) => ([...current, id]));
  }

  const handleSelectContacts = async () => {
    // Validate all action parameter forms before proceeding
    const allFormsValid = await validateAllActionForms();

    if (allFormsValid) {
      setCurrentStep(2);
    } else {
      // Forms are invalid, user will see validation errors
      console.log('Forms validation failed');
    }
  };

  const handleBackToSequenceSelection = () => {
    setCurrentStep(1);
  };

  const renderSequenceSelection = () => (
    <>

      <div className="mb-6">
        <Text strong className="mb-2 block">Sequence</Text>
        <DebounceSelect
          className="w-full"
          placeholder="Search and select a sequence..."
          fetchOptions={fetchSequenceOptions}
          value={selectedSequenceId}
          onChange={handleSequenceChange}
          allowClear
          size="large"
          labelInValue={false}
        />
      </div>

      {renderActionTimeline()}
    </>
  );

  const renderActionTimeline = () => {
    if (!selectedSequenceId) {
      return null;
    }

    if (sequenceLoading) {
      return <Skeleton active paragraph={{ rows: 5 }} />;
    }

    if (sequenceActions.length === 0) {
      return <Alert message="This sequence has no actions." type="info" showIcon={false} />;
    }

    return (
      <div>
        <ActionTimeline
          actions={sequenceActions}
          onFormsChange={handleActionFormsChange}
        />
      </div>
    );
  };

  const renderContactSelection = () => (
    <Flex vertical gap={16} className="mb-4">
      {renderSenderSection()}
      {companyId && renderRecipientSection()}
      {!companyId && (
        <Space.Compact>
          <Input value="To" className="w-[100px] text-black" disabled />
          <Select
            label="To"
            options={[{
              label: `First Contacts (${companiesCount} ${pluralize('company', companiesCount)})`,
              value: 'primary',
            }, {
              label: 'All Contacts',
              value: 'all',
            }]}
            className="w-full"
            value={selectedContactType}
            onChange={setSelectedContactType}
            size="large"
          />
        </Space.Compact>
      )}
    </Flex>
  );

  const renderSenderSection = () => (
    <Space.Compact>
      <Input value="From" className="w-[100px] text-black" disabled />
      <SelectAuthorizedEmail
        value={selectedSenderEmail}
        onChange={setSelectedSenderEmail}
        fixedOptions={FIXED_EMAIL_OPTIONS}
        mine
        autoSelectFirst
      />
    </Space.Compact>
  );

  const renderRecipientSection = () => (
    <>
      {
        !contacts?.length && (
          <Alert message="To run sequences effectively, please ensure that you have at least one contact in this company." type="info" showIcon={false} />
        )
      }
      <Flex gap={8} align="center" justify="space-between">
        <Text
          strong
          style={{
            visibility: contacts?.length > 0 ? 'visible' : 'hidden',
          }}
        >
          To
        </Text>
        <Flex gap={8}>
          {
            contacts?.length > 0 && (
              <ExpandableSearchInput
                placeholder="Search contacts by name, email, role or phone"
                onChange={setSearchText}
                value={searchText}
              />
            )
          }
          <Button
            disabled={isLoading('create_contact')}
            onClick={() => setCreateContactOpen(true)}
            icon={<PlusOutlined />}
            size="large"
          >
            Create contact
          </Button>
        </Flex>
      </Flex>
    </>

  );

  const renderContactsTable = () => {
    if (currentStep !== 2 || !selectedSequenceId || !contacts?.length) {
      return null;
    }

    return (
      <Table
        columns={contactsTableColumns}
        dataSource={filteredContacts}
        scroll={{ x: 780 }}
        rowKey="id"
        loading={contactsLoading}
        bordered
        rowSelection={{
          onChange: (selectedRowKeys) => {
            setSelectedContactIds((current) => {
              const newSelectedContactIds = [...current.filter((id) => !filteredContacts.some((contact) => contact.id === id))];
              return [...selectedRowKeys, ...newSelectedContactIds];
            });
          },
          selectedRowKeys: selectedContactIds,
          getCheckboxProps: () => ({
            disabled: contactsLoading || isLoading(),
          }),
        }}
        pagination={{
          showTotal: (total) => `Total ${total} results`,
          pageSize: 150,
        }}
      />
    );
  };

  const renderFooter = () => {
    // Check if all forms are valid (form validation handles required fields)
    const allFormsValid = sequenceActions.every((action) => {
      const formState = actionFormState[action.id];

      if (!action.definition) {
        // No parameters at all - always valid
        return true;
      }

      // Has parameters - check form validation state
      const isValid = formState?.isValid === true;
      return isValid;
    });

    function runIsDisabled() {
      if (isLoading()) {
        return true;
      }
      if (!selectedSequenceId) {
        return true;
      }
      if (companyId && selectedContactIds.length === 0) {
        return true;
      }
      return false;
    }

    return (
      <Flex justify="space-between">
        {currentStep === 1 && runMode !== RUN_MODES.CONTACT_LIST ? (
          <Flex justify="end" className="w-full gap-2">
            <Button
              onClick={onClose}
              disabled={isLoading('run_sequence')}
            >
              Cancel
            </Button>
            <Button
              disabled={!selectedSequenceId || !allFormsValid}
              type="primary"
              onClick={handleSelectContacts}
            >
              Select Contacts
            </Button>
          </Flex>
        ) : (
          <Flex justify="end" className="w-full gap-2">
            { runMode !== RUN_MODES.CONTACT_LIST && (
              <Button
                onClick={handleBackToSequenceSelection}
                disabled={isLoading('run_sequence')}
              >
                Previous
              </Button>
            )}
            <Button
              loading={isLoading('run_sequence')}
              disabled={runIsDisabled()}
              type="primary"
              onClick={handleRunSequence}
            >
              Confirm and Run
            </Button>
          </Flex>
        )}
      </Flex>
    );
  };

  const filteredContacts = useMemo(() => contacts?.filter(({
    name = '', email = '', role = '', phone = '',
  } = {}) => (!searchText || searchText.split(' ').some((token) => `${name} ${email} ${role} ${phone}`.toLowerCase().includes(token.toLowerCase())))), [contacts, searchText]);

  return (
    <Drawer
      title={`Run Sequence ${sequence?.name ? `: ${sequence.name}` : ''}`}
      placement="right"
      width={isMobile ? '100%' : 720}
      onClose={onClose}
      open={open}
      styles={{ body: { padding: '16px 24px' } }}
      footer={renderFooter()}
    >
      <Form form={form} layout="vertical">
        <div className={currentStep === 1 ? 'block' : 'hidden'}>
          <Flex vertical gap={24} className="mb-4">
            <Alert
              message="To execute predefined sequences, please choose the desired sequence first, and then select your target audiences."
              type="info"
              showIcon={false}
            />
            <Togglable
              mode="select"
              title="When should this sequence start?"
              showText="Start Later"
              hideText="Start Now"
              onVisibleChange={(visible) => {
                if (!visible) {
                  setScheduledFor(null);
                }
              }}
              className="m-0"
            >
              <DatePicker
                showTime={{ minuteStep: 15 }}
                placeholder="Select date and time"
                format="YYYY-MM-DD hh:mm A"
                use12Hours
                className="w-full"
                size="large"
                disabledDate={disabledDate}
                value={scheduledFor}
                onChange={setScheduledFor}
              />
            </Togglable>
            {runMode === RUN_MODES.CONTACT_LIST && renderSenderSection()}
            {renderSequenceSelection()}
          </Flex>
        </div>
        <div className={currentStep === 2 ? 'block' : 'hidden'}>
          {renderContactSelection()}
        </div>
      </Form>

      {companyId && renderContactsTable()}

      <ContactDrawerForm
        open={createContactOpen}
        onSubmit={handleAddContact}
        onClose={() => setCreateContactOpen(false)}
      />
    </Drawer>
  );
}

export default RunSequenceDrawer;
